import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  selectProductTour,
  setProductTourActive,
  setProductTourStep,
  setProductTourCompleted,
  selectAudioPermissionGranted,
  setAutoPlayUnlocker,
} from '../../reducers/uiSlice'

const tourSteps = [
  {
    selector: '[data-tour="chat-input"]',
    content: 'Aquí puedes escribir mensajes en el chat',
  },
  {
    selector: '[data-tour="share-button"]',
    content: 'Comparte esta transmisión con tus amigos usando este botón',
  },
  {
    selector: '[data-tour="catalog-button"]',
    content: 'Aquí puedes ver el catálogo de productos',
  },
  {
    selector: '[data-tour="like-button"]',
    content: 'Con este botón puedes enviar "me gusta" a la transmisión',
  },
  {
    selector: '[data-tour="pip-button"]',
    content: 'Aquí puedes usar el modo ventana flotante',
  },
]

export const ProductTour = ({ children }) => {
  const dispatch = useDispatch()
  const productTour = useSelector(selectProductTour)
  const audioPermissionGranted = useSelector(selectAudioPermissionGranted)
  const [targetElement, setTargetElement] = useState(null)

  useEffect(() => {
    if (!productTour.isActive) {
      setTargetElement(null)
      return
    }

    const currentStep = tourSteps[productTour.currentStep]
    if (!currentStep) {
      dispatch(setProductTourActive(false))
      return
    }

    const element = document.querySelector(currentStep.selector)
    if (element === null) {
      return
    } // Si no se encuentra el elemento, no hacer nada
    setTargetElement(element)

    // Auto avanzar después de 10 segundos
    const timer = setTimeout(() => {
      handleNext()
    }, 10000)

    return () => clearTimeout(timer)
  }, [productTour.isActive, productTour.currentStep])

  const handleNext = () => {
    if (audioPermissionGranted === false) {
      dispatch(setAutoPlayUnlocker({ requestUnmute: true, open: false }))
    }
    if (productTour.currentStep < tourSteps.length - 1) {
      const nextStep = tourSteps[productTour.currentStep + 1]
      const element = document.querySelector(nextStep.selector)
      if (element === null) {
        dispatch(setProductTourActive(false))
        return
      }
      dispatch(setProductTourStep(productTour.currentStep + 1))
    } else {
      handleClose()
    }
  }

  const handleClose = () => {
    dispatch(setProductTourActive(false))
    dispatch(setProductTourCompleted(true))
    sessionStorage.setItem('productTourCompleted', 'true')
  }

  if (!productTour.isActive || !targetElement) {
    return <>{children}</>
  }

  const rect = targetElement.getBoundingClientRect()

  // Calcular posición del popover
  const popoverWidth = 300
  const popoverHeight = 120 // Estimado

  // Determinar si hay espacio arriba o abajo
  const spaceAbove = rect.top
  const spaceBelow = window.innerHeight - rect.bottom
  const showAbove = spaceAbove > popoverHeight || spaceAbove > spaceBelow

  // Calcular posición horizontal (centrado con el elemento, pero sin salirse)
  let left = rect.left + rect.width / 2 - popoverWidth / 2
  if (left < 16) left = 16
  if (left + popoverWidth > window.innerWidth - 16) left = window.innerWidth - popoverWidth - 16

  // Calcular posición de la flecha relativa al popover
  const targetCenter = rect.left + rect.width / 2
  const popoverLeft = left
  const arrowOffset = targetCenter - popoverLeft

  const popoverStyle = showAbove
    ? {
        top: rect.top - 16,
        left: left,
        transform: 'translateY(-100%)',
      }
    : {
        top: rect.bottom + 16,
        left: left,
      }

  return (
    <>
      {children}
      <Spotlight
        style={{
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        }}
      />
      <Popover style={popoverStyle}>
        {showAbove && <PopoverArrowDown style={{ left: `${arrowOffset}px` }} />}
        <PopoverContent>{tourSteps[productTour.currentStep].content}</PopoverContent>
        <PopoverActions>
          <CloseButton onClick={handleClose}>×</CloseButton>
          <StepIndicator>
            {productTour.currentStep + 1} / {tourSteps.length}
          </StepIndicator>
          <NextButton onClick={handleNext}>
            {productTour.currentStep === tourSteps.length - 1 ? 'Finalizar' : 'Siguiente'}
          </NextButton>
        </PopoverActions>
        {!showAbove && <PopoverArrowUp style={{ left: `${arrowOffset}px` }} />}
      </Popover>
    </>
  )
}

const Spotlight = styled.div`
  position: fixed;
  border-radius: 8px;
  border: 2px solid #000;
  z-index: 9999;
  pointer-events: none;
`

const Popover = styled.div`
  position: fixed;
  background-color: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  max-width: 300px;
  color: black;
`

const PopoverArrowDown = styled.div`
  position: absolute;
  bottom: -8px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid white;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
`

const PopoverArrowUp = styled.div`
  position: absolute;
  top: -8px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid white;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
`

const PopoverContent = styled.div`
  margin-bottom: 1rem;
  font-size: 14px;
  line-height: 1.5;
`

const PopoverActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;

  &:hover {
    color: #000;
  }
`

const StepIndicator = styled.span`
  font-size: 12px;
  color: #666;
  flex: 1;
  text-align: center;
`

const NextButton = styled.button`
  background-color: #000;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 14px;
`

ProductTour.propTypes = {
  children: PropTypes.node.isRequired,
}
